/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState } from "react";
import { CellValue } from "@inscopix/ideas-hyperformula";
import { EuiDatePicker, EuiPopover } from "@inscopix/ideas-eui";
import { CellValueRendererBase } from "../cell-value-renderers/CellValueRendererBase";
import moment from "moment";

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDateFormat = (value: CellValue): value is string => {
  return typeof value === "string" && DATE_REGEX.test(value);
};

const styles = {
  popover: css`
    & {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
    }
  `,
  panel: css`
    max-height: max(200px, 50vh);
    overflow-y: scroll;
  `,
};

interface CellEditorDateProps {
  value: CellValue;
  onChangeFormula: (newFormula: string) => void;
  stopEditing: (cancel?: boolean) => void;
}

/** Cell editor for cells in date columns */
export const CellEditorDate = ({
  value,
  onChangeFormula,
  stopEditing,
}: CellEditorDateProps) => {
  const [newValue, setNewValue] = useState(value);

  return (
    <EuiPopover
      anchorPosition="downCenter"
      button={<CellValueRendererBase value={newValue} />}
      isOpen
      panelPaddingSize="none"
      css={styles.popover}
      closePopover={stopEditing}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          stopEditing(true);
        }
      }}
      display="block"
      repositionOnScroll={true}
      panelProps={{ paddingSize: "none" }}
    >
      <EuiDatePicker
        selected={
          isValidDateFormat(newValue) ? moment(newValue, "YYYY-MM-DD") : null
        }
        onChange={(date) => {
          if (newValue === date) {
            onChangeFormula("");
            setNewValue(null);
          } else {
            onChangeFormula(date ? date.format("YYYY-MM-DD") : "");
            setNewValue(date && date.format("YYYY-MM-DD"));
          }
        }}
        inline
      />
    </EuiPopover>
  );
};
